import {
    UserAgentStats,
    Channel,
    IntervalType,
    LogSummary,
    TrackedLink,
    ReportLocationStats,
    ExportFileFormats,
    CompressionFormat,
    ExportLink,
    InProgressChannelStats,
    ChannelChartData,
    TemplateLink,
    DeliveryLogType,
    StatusMessageCategory,
} from '@dev/ee-api/ee-api-models';
import { AxiosPromise } from 'axios';

import { ReportsClicksandopensByChannelnameGetParams } from '@/store/campaigns/types';
import { ChannelsListParams } from '@/store/activity/devices/types';
import { LocationReportsInterface } from '@/store/reports/types';
import { request } from './request';

export interface DevicesParams {
    from?: string;
    to?: string;
}

export const getUserAgentStats = (params: DevicesParams): AxiosPromise<UserAgentStats[]> => {
    const { Reports } = request();

    return Reports.reportsUseragentsGet(params.from, params.to);
};

export interface DevicesCampaignParams {
    campaignName: string;
    channelName?: string;
    from?: string;
    to?: string;
}

export const getUserAgentCampaignStats = (params: DevicesCampaignParams): AxiosPromise<UserAgentStats[]> => {
    const { Campaigns } = request();
    return Campaigns.campaignsByNameUseragentsGet(params.campaignName || params.channelName, params.from, params.to);
};

export const getAllChannels = (params: ChannelsListParams): AxiosPromise<Channel[]> => {
    const { Channels } = request();
    return Channels.channelsGet('', params.limit, params.offset);
};

export interface ReportsSummaryParams {
    from: string;
    to: string;
    channelName?: string;
    interval?: IntervalType;
    transactionID?: string;
}
export const getReportsSummary = (params: ReportsSummaryParams): AxiosPromise<LogSummary> => {
    const { Reports } = request();

    return Reports.reportsSummaryGet(params.from, params.to, params.channelName, params.interval, params.transactionID);
};

export interface LinkTrackingParams {
    linkSearch?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    channelName?: string;
    activePage?: number;
    perPage?: number;
}

export const getReportsLinkTrackings = (params: LinkTrackingParams): AxiosPromise<TrackedLink[]> => {
    const { Reports } = request();

    return Reports.reportsLinktrackingsGet(
        params.linkSearch,
        params.from,
        params.to,
        params.limit,
        params.offset,
        params.channelName
    );
};

export type ExportLinksParams = {
    from: string;
    to: string;
    channelName: string;
    fileFormat?: ExportFileFormats;
    compressionFormat?: CompressionFormat;
    fileName?: string;
    limit?: number;
    offset?: number;
};

export const exportLinks = (params: ExportLinksParams): AxiosPromise<ExportLink> => {
    const { Reports } = request();
    const { from, to, channelName, fileFormat, compressionFormat, fileName, limit, offset } = params;

    return Reports.reportsLinktrackingsExportGet(
        from,
        to,
        channelName,
        fileFormat,
        limit,
        offset,
        compressionFormat,
        fileName
    );
};

export type LinktrackingsAbsplitParams = {
    channelName: string;
    linkSearch?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    isUpdateUrlRequired?: boolean;
};

export const getLinktrackingsAbsplitByChannelName = (
    params: LinktrackingsAbsplitParams
): AxiosPromise<TemplateLink[]> => {
    const { Reports } = request();

    const { channelName, linkSearch, from, to, limit, offset } = params;
    return Reports.reportsLinktrackingsAbsplitByChannelnameGet(channelName, linkSearch, from, to, limit, offset);
};

export const getReportsLocationStatsByName = (
    params: LocationReportsInterface
): AxiosPromise<ReportLocationStats[]> => {
    const { Reports } = request();
    const { channelName, from, to, limit, offset, sortBy, sortDescending } = params;

    return Reports.reportsByChannelnameLocationsStatsGet(channelName, from, to, limit, offset, sortBy, sortDescending);
};

export const getInProgressByChannelName = (channelName: string): AxiosPromise<InProgressChannelStats> => {
    const { Reports } = request();

    return Reports.reportsInprogressByChannelnameGet(channelName);
};

export const getCampaignClicksAndOpensStatsByName = (
    params: ReportsClicksandopensByChannelnameGetParams
): AxiosPromise<ChannelChartData[]> => {
    const { Reports } = request();
    const { channelName, from, to, interval, onlyChildCampaigns, options } = params;

    return Reports.reportsClicksandopensByChannelnameGet(channelName, from, to, interval, onlyChildCampaigns, options);
};

export type ExportEventsParams = {
    channelName: string;
    statuses: DeliveryLogType[];
    messageCategory?: StatusMessageCategory[];
    from?: string;
    to?: string;
    email?: string;
    lastEvent?: boolean;
    fileFormat?: ExportFileFormats;
    compressionFormat?: CompressionFormat;
    fileName?: string;
};

export const exportEvents = (params: ExportEventsParams): AxiosPromise<ExportLink> => {
    const { Reports } = request();
    const {
        channelName,
        statuses,
        messageCategory,
        from,
        to,
        email,
        lastEvent,
        fileFormat,
        compressionFormat,
        fileName,
    } = params;

    return Reports.reportsEventsByChannelnameExportGet(
        channelName,
        statuses,
        messageCategory,
        from,
        to,
        email,
        lastEvent,
        fileFormat,
        compressionFormat,
        fileName
    );
};

export type ResendParams = {
    messageID: string;
    createNew?: boolean;
    activateContact?: boolean;
};

export const resendByMessageid = (params: ResendParams): AxiosPromise<void> => {
    const { Reports } = request();
    const { messageID, createNew, activateContact } = params;

    return Reports.reportsByMessageidResendPost(messageID, createNew, activateContact);
};
